import {
  CHECKLIST_LIMITS,
  CHECKLIST_PHOTO_TYPES,
  SYSTEM_CHECKLIST_TEMPLATES,
  checklistAlarmType,
  checklistCategory,
  checklistResult,
  effectiveChecklistRecipients,
  effectiveChecklistTemplate,
  effectiveShifts,
  formItems,
  naAllowed,
  normalizeRecipientEmail,
  shiftAt,
  validShift,
  validateChecklistAnswers,
  validateChecklistTemplate,
  type Alarm,
  type ChecklistAssignment,
  type ChecklistCategory,
  type ChecklistItem,
  type ChecklistRecipient,
  type ChecklistTemplate,
  type CurrentUser,
  type Site,
  type SiteShift,
  type TenantRole,
  type Vehicle,
} from "@fts/core";
import {
  ChecklistInvalidError,
  DataError,
  type ChecklistAnswerRow,
  type ChecklistFault,
  type ChecklistForm,
  type ChecklistPhoto,
  type ChecklistRecipientInput,
  type ChecklistSubmission,
  type ChecklistSubmissionDetail,
  type ChecklistSubmitInput,
  type ChecklistSubmitResult,
  type ChecklistTemplateInput,
  type ChecklistToken,
  type EffectiveChecklistRecipients,
  type FaultQuery,
  type ShiftInput,
  type SubmissionQuery,
} from "./provider";

/*
 * SAMPLE DATA ONLY — the pre-shift checklist of the demo mode (ADR 0016). Mirrors the rules of
 * supabase/migrations/20270222000001_checklists.sql with the pure helpers of packages/core. Photos stay in
 * the browser (object URLs). Nothing here (or anywhere) locks a vehicle (ADR 0003).
 */

/** What the demo checklist needs from the provider: identity, permissions, master data and alarms. */
export type DemoChecklistAccess = {
  me(): CurrentUser;
  hasTenantRole(tenantId: string, min: TenantRole): boolean;
  unscoped(tenantId: string, min: TenantRole): boolean;
  canAccessSite(siteId: string): boolean;
  canManageSite(siteId: string, min: TenantRole): boolean;
  sites(): Site[];
  vehicles(): Vehicle[];
  memberSeesSite(userId: string, tenantId: string, siteId: string): boolean;
  displayName(userId: string): string | null;
  /** Operator of the vehicle's current card session (live map), if any. */
  currentOperator(vehicleId: string): string | null;
  addAlarm(a: Omit<Alarm, "id">): void;
  hadImpactSince(vehicleId: string, since: number): boolean;
  now(): number;
  nextId(prefix: string): string;
};

const clone = <T>(x: T): T => JSON.parse(JSON.stringify(x)) as T;
const SAMPLE_PHOTO =
  "data:image/svg+xml;charset=utf-8," +
  encodeURIComponent('<svg xmlns="http://www.w3.org/2000/svg" width="320" height="200"><rect width="320" height="200" fill="#E2E2DF"/><text x="160" y="105" font-family="Arial" font-size="16" text-anchor="middle" fill="#5E6163">Örnek fotoğraf / Sample photo</text></svg>');

function systemTemplates(): ChecklistTemplate[] {
  return SYSTEM_CHECKLIST_TEMPLATES.map((t) => {
    const id = `ct-sys-${t.category}`;
    return {
      id,
      tenantId: null,
      systemCategory: t.category,
      name: t.nameTr,
      sourceTemplateId: null,
      items: t.items.map((i, position) => ({ ...i, powerTypes: [...i.powerTypes], id: `${id}-${i.code}`, templateId: id, position })),
      updatedAt: "2026-10-18T00:00:00Z",
    };
  });
}

export class DemoChecklist {
  private templates: ChecklistTemplate[] = systemTemplates();
  private assignments: ChecklistAssignment[] = [];
  private shifts: SiteShift[] = [];
  private recipients: ChecklistRecipient[] = [];
  private tokens = new Map<string, ChecklistToken>();
  private submissions: ChecklistSubmission[] = [];
  private answers: ChecklistAnswerRow[] = [];
  private photos: (ChecklistPhoto & { vehicleId: string; url: string; createdAt: number })[] = [];
  private faults: ChecklistFault[] = [];
  private clientKeys = new Map<string, string>();

  constructor(private readonly a: DemoChecklistAccess) {
    const vehicles = a.vehicles();
    // Deterministic sample tokens (32 hex characters, like the database's).
    vehicles.forEach((v, i) => this.tokens.set(v.id, { vehicleId: v.id, token: `de${(i + 1).toString(16).padStart(30, "0")}`, rotatedAt: null }));
    const t1 = vehicles[0]?.tenantId;
    const gebze = a.sites().find((s) => s.tenantId === t1 && s.parentId === null);
    if (t1 && gebze) {
      this.shifts.push(
        { id: "sh-day", tenantId: t1, siteId: gebze.id, name: "Gündüz", start: "08:00", end: "16:00", weekdays: [1, 2, 3, 4, 5, 6], tz: "Europe/Istanbul" },
        { id: "sh-eve", tenantId: t1, siteId: gebze.id, name: "Akşam", start: "16:00", end: "00:00", weekdays: [1, 2, 3, 4, 5], tz: "Europe/Istanbul" },
      );
      this.recipients.push(
        { id: "cr-1", tenantId: t1, siteId: gebze.id, userId: "u-mehmet", email: null, label: null },
        { id: "cr-2", tenantId: t1, siteId: gebze.id, userId: null, email: "bakim@ornek-servis.example", label: "Örnek Bakım Servisi" },
      );
      this.seedHistory(vehicles.filter((v) => v.tenantId === t1));
    }
  }

  /** Two sample checklists: one with an open critical fault ("Bakım gerekli"), one all OK. */
  private seedHistory(vehicles: Vehicle[]) {
    const now = this.a.now();
    const add = (v: Vehicle | undefined, hoursAgo: number, fails: Record<string, string>) => {
      if (!v) return;
      const tpl = this.templateFor(v);
      if (!tpl) return;
      const items = formItems(tpl.items, v.power, false);
      const id = `cs-seed-${v.id}`;
      const at = new Date(now - hoursAgo * 3600_000).toISOString();
      const answers = items.map((i) => ({ itemId: i.id, answer: fails[i.code] ? ("not_ok" as const) : ("ok" as const), note: fails[i.code] ?? null }));
      const result = checklistResult(items, answers);
      this.submissions.push({
        id,
        tenantId: v.tenantId,
        siteId: v.siteId,
        vehicleId: v.id,
        templateName: tpl.name,
        category: checklistCategory(v.type, v.power),
        shiftName: "Gündüz",
        shiftStart: at,
        submittedAt: at,
        operatorId: null,
        operatorUnknown: true,
        result,
        impactSinceLast: false,
      });
      items.forEach((i, position) => {
        const ans = answers[position]!;
        const answerId = `${id}-a${position}`;
        this.answers.push({ id: answerId, submissionId: id, position, code: i.code, textTr: i.textTr, textEn: i.textEn, critical: i.critical, photoRequiredOnFail: i.photoRequiredOnFail, answer: ans.answer, note: ans.note });
        if (ans.answer === "not_ok") {
          this.faults.push({
            id: `cf-${answerId}`,
            tenantId: v.tenantId,
            siteId: v.siteId,
            vehicleId: v.id,
            submissionId: id,
            answerId,
            itemCode: i.code,
            textTr: i.textTr,
            textEn: i.textEn,
            critical: i.critical,
            note: ans.note,
            status: "open",
            createdAt: at,
            resolvedBy: null,
            resolvedAt: null,
            resolutionNote: null,
          });
          if (i.photoRequiredOnFail) this.photos.push({ id: `cp-${answerId}`, submissionId: id, answerId, path: `sample/${answerId}.svg`, contentType: "image/png", vehicleId: v.id, url: SAMPLE_PHOTO, createdAt: now });
        }
      });
    };
    add(vehicles.find((v) => v.fleetNo === "FL-02"), 3, { "3": "Örnek not: sol ön lastikte kesik." });
    add(vehicles.find((v) => v.fleetNo === "FL-01"), 5, {});
  }

  private templateFor(v: Vehicle): ChecklistTemplate | undefined {
    const cat = checklistCategory(v.type, v.power);
    const own = this.templates.filter((t) => t.tenantId === null || t.tenantId === v.tenantId);
    const id = effectiveChecklistTemplate(cat, this.assignments.filter((x) => x.tenantId === v.tenantId), own);
    return this.templates.find((t) => t.id === id);
  }

  /** Like fts_checklist_form_version: changes with any shown item (demo: a simple string hash). */
  private version(tpl: ChecklistTemplate | undefined, v: Vehicle): string {
    if (!tpl) return "0".repeat(32);
    const text = formItems(tpl.items, v.power, false)
      .map((i) => [i.id, i.position, i.code, i.textTr, i.textEn, i.critical, i.photoRequiredOnFail, i.allowNa, i.impactFirst].join("|"))
      .join(";");
    let h = 0;
    for (let k = 0; k < text.length; k++) h = (Math.imul(h, 31) + text.charCodeAt(k)) >>> 0;
    return `${tpl.id}:${h}`.split("").reduce((a, c) => a + c.charCodeAt(0).toString(16), "").padEnd(32, "0").slice(0, 32);
  }

  private vehicleByToken(token: string): Vehicle {
    const entry = [...this.tokens.values()].find((x) => x.token === token);
    const v = entry && this.a.vehicles().find((x) => x.id === entry.vehicleId && x.active);
    if (!v) throw new DataError("checklist_token_invalid");
    return v;
  }

  private shiftsOf(siteId: string): SiteShift[] {
    return effectiveShifts(this.a.sites(), this.shifts, siteId).shifts;
  }

  /** Sprint 7b sample reports: the shifts that apply to a site (inherited), like fts_private.shift_instances. */
  effectiveShiftsOf(siteId: string): SiteShift[] {
    return clone(this.shiftsOf(siteId));
  }

  private impactSinceLast(v: Vehicle): boolean {
    const last = Math.max(0, ...this.submissions.filter((s) => s.vehicleId === v.id).map((s) => Date.parse(s.submittedAt)));
    return this.a.hadImpactSince(v.id, last || this.a.now() - 7 * 86_400_000);
  }

  private recordVisible(r: { tenantId: string; siteId: string | null }): boolean {
    return this.a.hasTenantRole(r.tenantId, "viewer") && (r.siteId === null ? this.a.unscoped(r.tenantId, "viewer") : this.a.canAccessSite(r.siteId));
  }

  /* ---------- public form ---------- */

  form(token: string): ChecklistForm {
    const v = this.vehicleByToken(token);
    const tpl = this.templateFor(v);
    const impact = this.impactSinceLast(v);
    const now = new Date(this.a.now());
    const shift = shiftAt(this.shiftsOf(v.siteId), now);
    return {
      vehicle: { fleetNo: v.fleetNo, type: v.type, power: v.power, siteName: this.a.sites().find((s) => s.id === v.siteId)?.name ?? null },
      template: tpl ? { id: tpl.id, name: tpl.name, nameEn: SYSTEM_CHECKLIST_TEMPLATES.find((x) => x.category === tpl.systemCategory)?.nameEn ?? tpl.name } : null,
      items: tpl
        ? formItems(tpl.items, v.power, impact).map((i) => ({ id: i.id, code: i.code, textTr: i.textTr, textEn: i.textEn, photoRequiredOnFail: i.photoRequiredOnFail, naAllowed: naAllowed(i) }))
        : [],
      version: this.version(tpl, v),
      impactSinceLast: impact,
      openFaults: this.faults
        .filter((f) => f.vehicleId === v.id && f.status === "open")
        .sort((x, y) => Date.parse(y.createdAt) - Date.parse(x.createdAt))
        .map((f) => ({ code: f.itemCode, textTr: f.textTr, textEn: f.textEn, createdAt: f.createdAt })),
      shift: shift ? { name: shift.name, startsAt: shift.startsAt.toISOString(), endsAt: shift.endsAt.toISOString() } : null,
      submittedThisShift: !!shift && this.submissions.some((s) => s.vehicleId === v.id && s.shiftStart === shift.startsAt.toISOString()),
    };
  }

  uploadPhoto(token: string, file: Blob): string {
    const v = this.vehicleByToken(token);
    if (file.size > CHECKLIST_LIMITS.photoMaxBytes || !(CHECKLIST_PHOTO_TYPES as readonly string[]).includes(file.type)) throw new DataError("invalid_photo");
    const id = this.a.nextId("cp");
    let url = SAMPLE_PHOTO;
    try {
      if (typeof URL.createObjectURL === "function") url = URL.createObjectURL(file);
    } catch {
      // Keep the sample picture.
    }
    this.photos.push({ id, submissionId: null, answerId: null, path: `demo/${v.id}/${id}`, contentType: file.type, vehicleId: v.id, url, createdAt: this.a.now() });
    return id;
  }

  submit(token: string, input: ChecklistSubmitInput): ChecklistSubmitResult {
    const v = this.vehicleByToken(token);
    // Same key again (retry, double tap): the original submission, nothing new.
    const prev = this.clientKeys.get(`${v.id}:${input.clientSubmissionId}`);
    if (prev) return { submissionId: prev };
    const tpl = this.templateFor(v);
    if (!tpl) throw new DataError("invalid_checklist");
    if (input.formVersion && input.formVersion !== this.version(tpl, v)) throw new DataError("form_changed");
    const impact = this.impactSinceLast(v);
    const items = formItems(tpl.items, v.power, impact);
    const issues = validateChecklistAnswers(items, input.answers);
    if (issues.length) throw new ChecklistInvalidError(issues);
    const photoIds = input.answers.flatMap((x) => x.photoIds ?? []);
    if (photoIds.some((p) => !this.photos.some((ph) => ph.id === p && ph.vehicleId === v.id && ph.submissionId === null)) || new Set(photoIds).size !== photoIds.length) {
      throw new DataError("invalid_photo");
    }
    const now = new Date(this.a.now());
    const shift = shiftAt(this.shiftsOf(v.siteId), now);
    const result = checklistResult(items, input.answers);
    const operatorId = this.a.currentOperator(v.id);
    const id = this.a.nextId("cs");
    this.submissions.push({
      id,
      tenantId: v.tenantId,
      siteId: v.siteId,
      vehicleId: v.id,
      templateName: tpl.name,
      category: checklistCategory(v.type, v.power),
      shiftName: shift?.name ?? null,
      shiftStart: shift?.startsAt.toISOString() ?? null,
      submittedAt: now.toISOString(),
      operatorId,
      operatorUnknown: operatorId === null,
      result,
      impactSinceLast: impact,
    });
    items
      .slice()
      .sort((x, y) => x.position - y.position)
      .forEach((item: ChecklistItem) => {
        const ans = input.answers.find((x) => x.itemId === item.id)!;
        const answerId = this.a.nextId("ca");
        const note = ans.note?.trim() || null;
        this.answers.push({ id: answerId, submissionId: id, position: item.position, code: item.code, textTr: item.textTr, textEn: item.textEn, critical: item.critical, photoRequiredOnFail: item.photoRequiredOnFail, answer: ans.answer, note });
        for (const p of this.photos) if ((ans.photoIds ?? []).includes(p.id)) Object.assign(p, { submissionId: id, answerId });
        if (ans.answer === "not_ok") {
          this.faults.push({
            id: this.a.nextId("cf"),
            tenantId: v.tenantId,
            siteId: v.siteId,
            vehicleId: v.id,
            submissionId: id,
            answerId,
            itemCode: item.code,
            textTr: item.textTr,
            textEn: item.textEn,
            critical: item.critical,
            note,
            status: "open",
            createdAt: now.toISOString(),
            resolvedBy: null,
            resolvedAt: null,
            resolutionNote: null,
          });
        }
      });
    const type = checklistAlarmType(result);
    if (type) {
      this.a.addAlarm({
        tenantId: v.tenantId,
        siteId: v.siteId,
        vehicleId: v.id,
        deviceId: v.deviceId,
        eventId: null,
        type,
        severity: 2,
        occurredAt: now.toISOString(),
        receivedAt: now.toISOString(),
        operatorId,
        offHours: false,
        delayed: false,
        timeUnknown: false,
        repeatCount: 0,
        lastOccurredAt: now.toISOString(),
        notifyError: null,
        status: "open",
        ackedBy: null,
        ackedAt: null,
        ackNote: null,
        createdAt: now.toISOString(),
        geofenceId: null,
        lat: null,
        lon: null,
        checklistSubmissionId: id,
        shiftName: shift?.name ?? null,
        shiftStart: shift?.startsAt.toISOString() ?? null,
      });
    }
    this.clientKeys.set(`${v.id}:${input.clientSubmissionId}`, id);
    return { submissionId: id };
  }

  /* ---------- shifts ---------- */

  listShifts(tenantId: string): SiteShift[] {
    return clone(this.shifts.filter((s) => s.tenantId === tenantId && this.a.canAccessSite(s.siteId)));
  }

  saveShift(input: ShiftInput): SiteShift {
    const existing = input.id ? this.shifts.find((s) => s.id === input.id) : undefined;
    const siteId = existing?.siteId ?? input.siteId;
    const site = this.a.sites().find((s) => s.id === siteId);
    if (!site || site.tenantId !== input.tenantId || !this.a.canManageSite(siteId, "fleet_manager")) throw new DataError("forbidden");
    if (!validShift(input)) throw new DataError("invalid_shift");
    if (!existing && this.shifts.filter((s) => s.siteId === siteId).length >= CHECKLIST_LIMITS.shiftsPerSite) throw new DataError("too_many_shifts");
    const row: SiteShift = { id: existing?.id ?? this.a.nextId("sh"), tenantId: site.tenantId, siteId, name: input.name.trim(), start: input.start, end: input.end, weekdays: [...new Set(input.weekdays)].sort(), tz: input.tz };
    if (existing) Object.assign(existing, row);
    else this.shifts.push(row);
    return clone(row);
  }

  deleteShift(id: string): void {
    const s = this.shifts.find((x) => x.id === id);
    if (!s || !this.a.canManageSite(s.siteId, "fleet_manager")) throw new DataError("forbidden");
    this.shifts = this.shifts.filter((x) => x.id !== id);
  }

  /* ---------- recipients ---------- */

  private recipientVisible(r: ChecklistRecipient): boolean {
    return r.email === null ? this.a.canAccessSite(r.siteId) : this.a.canManageSite(r.siteId, "fleet_manager");
  }

  listRecipients(tenantId: string): ChecklistRecipient[] {
    return clone(this.recipients.filter((r) => r.tenantId === tenantId && this.recipientVisible(r)));
  }

  addRecipient(input: ChecklistRecipientInput): ChecklistRecipient {
    const site = this.a.sites().find((s) => s.id === input.siteId);
    if (!site || site.tenantId !== input.tenantId) throw new DataError("forbidden");
    const email = input.email === null ? null : normalizeRecipientEmail(input.email);
    if (!this.a.canManageSite(site.id, email !== null || input.email !== null ? "admin" : "fleet_manager")) throw new DataError("forbidden");
    if ((input.userId === null) === (input.email === null) || (input.email !== null && !email)) throw new DataError("invalid_recipient");
    if (input.userId && !this.a.memberSeesSite(input.userId, site.tenantId, site.id)) throw new DataError("invalid_recipient");
    const own = this.recipients.filter((r) => r.siteId === site.id);
    if (own.length >= CHECKLIST_LIMITS.recipientsPerSite || own.some((r) => (input.userId && r.userId === input.userId) || (email && r.email === email))) throw new DataError("invalid_recipient");
    const row: ChecklistRecipient = { id: this.a.nextId("cr"), tenantId: site.tenantId, siteId: site.id, userId: input.userId, email, label: input.label?.trim() || null };
    this.recipients.push(row);
    return clone(row);
  }

  deleteRecipient(id: string): void {
    const r = this.recipients.find((x) => x.id === id);
    if (!r || !this.a.canManageSite(r.siteId, r.email === null ? "fleet_manager" : "admin")) throw new DataError("forbidden");
    this.recipients = this.recipients.filter((x) => x.id !== id);
  }

  effectiveRecipients(siteId: string): EffectiveChecklistRecipients {
    const site = this.a.sites().find((s) => s.id === siteId);
    if (!site || !this.a.canAccessSite(siteId)) return { recipients: [], sourceSiteId: null, externalSourceSiteId: null };
    const reachable = (r: ChecklistRecipient) => r.userId !== null && this.a.memberSeesSite(r.userId, site.tenantId, siteId);
    const eff = effectiveChecklistRecipients(this.a.sites(), this.recipients.filter((r) => r.tenantId === site.tenantId), siteId, reachable);
    const mgr = this.a.canManageSite(siteId, "fleet_manager");
    return {
      recipients: [
        ...eff.members.map((r) => ({ recipientId: r.id, userId: r.userId, displayName: this.a.displayName(r.userId!) ?? "—", email: null, sourceSiteId: r.siteId })),
        ...eff.externals.map((r) => ({ recipientId: r.id, userId: null, displayName: mgr ? r.label : null, email: mgr ? r.email : null, sourceSiteId: r.siteId })),
      ],
      sourceSiteId: eff.memberSourceSiteId,
      externalSourceSiteId: eff.externalSourceSiteId,
    };
  }

  /* ---------- templates ---------- */

  listTemplates(tenantId: string): ChecklistTemplate[] {
    if (!this.a.hasTenantRole(tenantId, "viewer")) throw new DataError("forbidden");
    return clone(this.templates.filter((t) => t.tenantId === null || t.tenantId === tenantId));
  }

  copyTemplate(tenantId: string, templateId: string, name: string | null): string {
    const src = this.templates.find((t) => t.id === templateId);
    if (!src || !this.a.unscoped(tenantId, "fleet_manager") || (src.tenantId !== null && src.tenantId !== tenantId)) throw new DataError("forbidden");
    const id = this.a.nextId("ct");
    this.templates.push({
      id,
      tenantId,
      systemCategory: null,
      name: (name?.trim() || src.name).slice(0, CHECKLIST_LIMITS.templateNameMax),
      sourceTemplateId: src.id,
      items: src.items.map((i) => ({ ...clone(i), id: this.a.nextId("ci"), templateId: id })),
      updatedAt: new Date(this.a.now()).toISOString(),
    });
    return id;
  }

  saveTemplate(input: ChecklistTemplateInput): void {
    const t = this.templates.find((x) => x.id === input.id);
    if (!t || t.tenantId === null || !this.a.unscoped(t.tenantId, "fleet_manager")) throw new DataError("forbidden");
    if (validateChecklistTemplate(input.name, input.items).length) throw new DataError("invalid_template");
    t.name = input.name.trim();
    t.items = input.items.map((i, position) => ({
      id: i.id && t.items.some((x) => x.id === i.id) ? i.id : this.a.nextId("ci"),
      templateId: t.id,
      position,
      code: i.code.trim().toUpperCase(),
      textTr: i.textTr.trim(),
      textEn: i.textEn.trim(),
      critical: i.critical,
      photoRequiredOnFail: i.photoRequiredOnFail,
      powerTypes: [...i.powerTypes],
      impactFirst: i.impactFirst,
      allowNa: i.allowNa,
    }));
    t.updatedAt = new Date(this.a.now()).toISOString();
  }

  deleteTemplate(id: string): void {
    const t = this.templates.find((x) => x.id === id);
    if (!t || t.tenantId === null || !this.a.unscoped(t.tenantId, "fleet_manager")) throw new DataError("forbidden");
    this.templates = this.templates.filter((x) => x.id !== id);
    this.assignments = this.assignments.filter((x) => x.templateId !== id);
  }

  listAssignments(tenantId: string): ChecklistAssignment[] {
    if (!this.a.hasTenantRole(tenantId, "viewer")) throw new DataError("forbidden");
    return clone(this.assignments.filter((x) => x.tenantId === tenantId));
  }

  setAssignment(tenantId: string, category: ChecklistCategory, templateId: string | null): void {
    if (!this.a.unscoped(tenantId, "fleet_manager")) throw new DataError("forbidden");
    this.assignments = this.assignments.filter((x) => !(x.tenantId === tenantId && x.category === category));
    if (templateId === null) return;
    const t = this.templates.find((x) => x.id === templateId);
    if (!t || (t.tenantId !== null && t.tenantId !== tenantId)) throw new DataError("invalid_template");
    this.assignments.push({ tenantId, category, templateId });
  }

  /* ---------- records ---------- */

  listSubmissions(tenantId: string, q: SubmissionQuery = {}): ChecklistSubmission[] {
    return clone(
      this.submissions
        .filter((s) => s.tenantId === tenantId && this.recordVisible(s) && (!q.vehicleId || s.vehicleId === q.vehicleId))
        .sort((x, y) => Date.parse(y.submittedAt) - Date.parse(x.submittedAt))
        .slice(0, q.limit ?? 200),
    );
  }

  getSubmission(id: string): ChecklistSubmissionDetail | null {
    const s = this.submissions.find((x) => x.id === id);
    if (!s || !this.recordVisible(s)) return null;
    return clone({
      submission: s,
      answers: this.answers.filter((x) => x.submissionId === id).sort((x, y) => x.position - y.position),
      photos: this.photos.filter((p) => p.submissionId === id).map(({ id: pid, submissionId, answerId, path, contentType }) => ({ id: pid, submissionId, answerId, path, contentType })),
    });
  }

  listFaults(tenantId: string, q: FaultQuery = {}): ChecklistFault[] {
    return clone(
      this.faults
        .filter((f) => f.tenantId === tenantId && this.recordVisible(f))
        .filter((f) => (!q.status || f.status === q.status) && (!q.vehicleId || f.vehicleId === q.vehicleId))
        .sort((x, y) => Date.parse(y.createdAt) - Date.parse(x.createdAt))
        .slice(0, q.limit ?? 500),
    );
  }

  resolveFault(id: string, note: string | null): void {
    const f = this.faults.find((x) => x.id === id);
    const allowed = f && this.a.hasTenantRole(f.tenantId, "fleet_manager") && (f.siteId === null ? this.a.unscoped(f.tenantId, "fleet_manager") : this.a.canAccessSite(f.siteId));
    if (!f || !allowed) throw new DataError("forbidden");
    if (f.status !== "open") throw new DataError("fault_resolved");
    const trimmed = note?.trim() || null;
    if (trimmed && trimmed.length > CHECKLIST_LIMITS.resolutionNoteMax) throw new DataError("note_too_long");
    Object.assign(f, { status: "resolved", resolvedBy: this.a.me().id, resolvedAt: new Date(this.a.now()).toISOString(), resolutionNote: trimmed });
  }

  photoUrl(path: string): string {
    const p = this.photos.find((x) => x.path === path);
    const s = p?.submissionId ? this.submissions.find((x) => x.id === p.submissionId) : undefined;
    if (!p || !s || !this.recordVisible(s)) throw new DataError("forbidden");
    return p.url;
  }

  listTokens(tenantId: string): ChecklistToken[] {
    return clone(
      [...this.tokens.values()].filter((t) => {
        const v = this.a.vehicles().find((x) => x.id === t.vehicleId);
        return !!v && v.tenantId === tenantId && this.a.canManageSite(v.siteId, "fleet_manager");
      }),
    );
  }

  rotateToken(vehicleId: string): string {
    const v = this.a.vehicles().find((x) => x.id === vehicleId);
    if (!v || !this.a.hasTenantRole(v.tenantId, "admin") || !this.a.canAccessSite(v.siteId)) throw new DataError("forbidden");
    const bytes = new Uint8Array(16);
    crypto.getRandomValues(bytes);
    const token = [...bytes].map((b) => b.toString(16).padStart(2, "0")).join("");
    this.tokens.set(vehicleId, { vehicleId, token, rotatedAt: new Date(this.a.now()).toISOString() });
    return token;
  }

  /** New vehicles get a token like the database trigger. */
  ensureToken(vehicleId: string): void {
    if (this.tokens.has(vehicleId)) return;
    const bytes = new Uint8Array(16);
    crypto.getRandomValues(bytes);
    this.tokens.set(vehicleId, { vehicleId, token: [...bytes].map((b) => b.toString(16).padStart(2, "0")).join(""), rotatedAt: null });
  }
}
